import type { MemberLoad, WorkspaceMember, WorkspaceMembership } from '@bb/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/features/auth/use-current-user';
import { useSchedules } from '@/features/booking/api';
import {
  useInviteMember,
  useLeaveWorkspace,
  useMembers,
  useMemberships,
  useMembersLoad,
  usePastMembers,
  useRemoveMember,
  useResendInvitation,
  useSaveMemberSettings,
} from '@/features/workspace/api';
import { ApiRequestError } from '@/lib/api';
import {
  btnDanger,
  btnDangerSolid,
  btnPrimary,
  btnSecondary,
  btnSmall,
  cardCls,
  inputCls,
  labelCls,
} from '@/lib/ui';

/**
 * The people who can take bookings in this workspace.
 *
 * A member keeps their own account, their own calendars and their own working
 * hours; joining only lets this workspace's pages offer the times they are
 * free. The screen says so, because "share my calendar with my boss" is a
 * reasonable thing to be wary of and the answer is narrower than it sounds.
 */
export const Route = createFileRoute('/app/settings/members')({
  component: MembersPage,
});

function MembersPage() {
  const { t } = useTranslation(['account', 'errors']);
  const { data: members, isPending } = useMembers();
  const { data: load = [] } = useMembersLoad();
  const loads = new Map(load.map((l) => [l.id, l]));
  const user = useCurrentUser();
  // Inviting is subscriber-only; reading and tidying up are not, so the form is
  // what gets replaced rather than the whole screen.
  const subscribed = user.data?.billing.paid ?? false;

  /*
    This tab serves two people who want opposite things from it. Somebody who
    has joined one workspace and owns nothing was being shown "invite other
    people to take bookings" and a prompt to subscribe, above their own
    settings. Their own section goes first for them.
  */
  const joined = (useMemberships().data ?? []).length > 0;

  /*
    Nothing of the owner's half works without a business name. Every booking a
    member takes would go out under the owner's own name, so the field is asked
    for first rather than left to be discovered in a customer's inbox.

    It stands in for the owner's half and nothing else. "Workspaces you have
    joined" is this person's membership of somebody else's business and has no
    bearing on whether they have named their own.
  */
  const ownerHalf = !user.data?.organizationName ? (
    <section className={cardCls}>
      <h2 className="text-sm font-medium">{t('account:members.needsOrgTitle')}</h2>
      <p className="mt-2 text-sm text-neutral-600">{t('account:members.needsOrgBody')}</p>
      <Link to="/app/settings" search={{ tab: 'profile' }} className={`${btnPrimary} mt-3`}>
        {t('account:members.needsOrgAction')}
      </Link>
    </section>
  ) : (
    <>
      <p className="text-sm text-neutral-600">{t('account:members.body')}</p>

      {subscribed ? <InviteForm /> : <SubscribePrompt />}

      <section className={cardCls}>
        <h2 className="text-sm font-medium">{t('account:members.listTitle')}</h2>
        {isPending ? (
          <p className="mt-3 text-sm text-neutral-500">{t('account:members.loading')}</p>
        ) : members && members.length > 0 ? (
          <ul className="mt-3 divide-y divide-neutral-200">
            {members.map((member) => (
              <MemberRow key={member.id} member={member} load={loads.get(member.id) ?? null} />
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-neutral-500">{t('account:members.empty')}</p>
        )}
      </section>

      {/* Below the live list: a record, not something to act on. */}
      <PastMembers />
    </>
  );

  return (
    <div className="space-y-6">
      {joined ? <JoinedWorkspaces /> : null}
      {ownerHalf}
      {joined ? null : <JoinedWorkspaces />}
    </div>
  );
}

/**
 * Memberships that have ended.
 *
 * The rows were already being kept — removing somebody marks the row rather
 * than deleting it, so that a later seat model could still count what a
 * workspace had — but nothing could read them. Shown quietly, below the live
 * list, because it is a record rather than something to act on.
 */
function PastMembers() {
  const { t } = useTranslation(['account']);
  const { data: past = [] } = usePastMembers();
  const [open, setOpen] = useState(false);

  if (past.length === 0) return null;

  return (
    <section className={cardCls}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-left text-sm font-medium"
      >
        <span>{t('account:members.pastTitle', { count: past.length })}</span>
        <span aria-hidden className="text-neutral-400">
          {open ? '−' : '+'}
        </span>
      </button>
      {open ? (
        <ul className="mt-3 divide-y divide-neutral-200">
          {past.map((m) => (
            <li key={m.id} className="py-2.5">
              <p className="text-sm text-neutral-700">{m.name ?? m.email}</p>
              {m.name ? <p className="text-xs text-neutral-500">{m.email}</p> : null}
              <p className="mt-0.5 text-xs text-neutral-500">
                {/* How it ended, which the status alone cannot say — "Yuki left"
                    and "you removed Yuki" are not the same thing a month on. */}
                {t(`account:members.endedBy.${m.endedBy ?? 'owner'}`)}
                {m.removedAt ? ` · ${new Date(m.removedAt).toLocaleDateString()}` : ''}
              </p>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * The other side of this screen: workspaces you have joined, rather than people
 * who joined yours.
 *
 * Hidden when there are none, which is almost everybody. It lives on this tab
 * because "members" is one idea seen from two ends, and a member looking for
 * the way out will look where they were added.
 */
function JoinedWorkspaces() {
  const { t } = useTranslation(['account', 'common', 'errors']);
  const { data: memberships = [] } = useMemberships();
  const leave = useLeaveWorkspace();
  const [confirming, setConfirming] = useState<string | null>(null);
  /*
    Closed until asked for. Somebody whose calendar is connected never touches
    these — the tool comes from the calendar, the business has their number —
    so three fields under every row is three questions a member did not have.
  */
  const [editing, setEditing] = useState<string | null>(null);

  if (memberships.length === 0) return null;

  const error = leave.error instanceof ApiRequestError ? leave.error : null;

  return (
    <section className={cardCls}>
      <h2 className="text-sm font-medium">{t('account:members.joinedTitle')}</h2>
      <p className="mt-1 text-xs text-neutral-500">{t('account:members.joinedBody')}</p>
      <ul className="mt-3 divide-y divide-neutral-200">
        {memberships.map((w) => (
          <li key={w.id} className="py-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{w.ownerName}</p>
                <p className="mt-0.5 text-xs text-neutral-500">
                  {t('account:members.joined', { date: new Date(w.joinedAt).toLocaleDateString() })}
                </p>
              </div>
              {/*
                One thing at a time. Somebody editing their phone number is not
                also deciding whether to leave, and the way out of this
                business should not sit beside a text field they are typing in.
              */}
              {confirming === w.id ? null : (
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing(editing === w.id ? null : w.id)}
                    className={btnSmall}
                    aria-expanded={editing === w.id}
                  >
                    {editing === w.id
                      ? t('common:actions.done')
                      : t('account:members.editSettings')}
                  </button>
                  {editing === w.id ? null : (
                    <button type="button" onClick={() => setConfirming(w.id)} className={btnSmall}>
                      {t('account:members.leave')}
                    </button>
                  )}
                </div>
              )}
            </div>
            {editing === w.id ? <MembershipSettings membership={w} /> : null}
            {confirming === w.id ? (
              <LeaveConfirm
                membership={w}
                pending={leave.isPending}
                error={error}
                onConfirm={() => leave.mutate(w.id)}
                onCancel={() => setConfirming(null)}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * What leaving actually does, before it is done.
 *
 * Leaving is not an undo: coming back needs the business to invite the person
 * again, and anything they set for it is gone in the meantime. It also reaches
 * other people — pages they were the only host of stop taking bookings — and
 * none of that is visible from a button marked "Leave". So the row says it,
 * and the way out stays one deliberate click away rather than none.
 */
function LeaveConfirm({
  membership,
  pending,
  error,
  onConfirm,
  onCancel,
}: {
  membership: WorkspaceMembership;
  pending: boolean;
  error: ApiRequestError | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation(['account', 'common', 'errors']);
  const effects = [
    'leaveEffectPages',
    'leaveEffectOwner',
    'leaveEffectSettings',
    'leaveEffectBack',
  ];

  return (
    <div className="mt-3 rounded border border-red-200 bg-red-50 p-3">
      <p className="text-sm font-medium text-red-900">
        {t('account:members.leaveTitle', { name: membership.ownerName })}
      </p>
      <ul className="mt-2 list-disc space-y-1 ps-5 text-xs text-red-900">
        {effects.map((key) => (
          <li key={key}>{t(`account:members.${key}`)}</li>
        ))}
      </ul>
      {error ? (
        <p className="mt-2 text-xs font-medium text-red-700">{t(`errors:${error.code}`)}</p>
      ) : null}
      <div className="mt-3 flex items-center gap-2">
        <button type="button" onClick={onConfirm} disabled={pending} className={btnDangerSolid}>
          {t('account:members.confirmLeave', { name: membership.ownerName })}
        </button>
        <button type="button" onClick={onCancel} className={btnSecondary}>
          {t('common:actions.cancel')}
        </button>
      </div>
    </div>
  );
}

/**
 * How this person does the job for this business.
 *
 * All three sit together because they answer one question, and because this row
 * is the only screen a member has. Saved on blur rather than behind a button:
 * three fields with one Save each reads as a form, and this is a handful of
 * preferences.
 */
function MembershipSettings({ membership }: { membership: WorkspaceMembership }) {
  const { t } = useTranslation(['account', 'booking', 'errors']);
  const save = useSaveMemberSettings();
  // Their own schedules: this picks between hours they have already set up
  // rather than being another place to write working hours.
  const { data: schedules = [] } = useSchedules();
  const usual = schedules.find((s) => s.isDefault);
  const [phone, setPhone] = useState(membership.settings.phone ?? '');
  const [note, setNote] = useState(membership.settings.note ?? '');

  /*
    In the server's order of preference, so the first survivor of the filter is
    what a booking would actually use when nothing is chosen. Zoom first because
    connecting it is a deliberate act; Meet and Teams come with the calendar.
  */
  const tools = [
    { value: 'zoom', label: 'Zoom', can: membership.canUse.zoom },
    { value: 'google_meet', label: 'Google Meet', can: membership.canUse.googleMeet },
    { value: 'teams', label: 'Microsoft Teams', can: membership.canUse.teams },
  ].filter((o) => o.can);

  const error = save.error instanceof ApiRequestError ? save.error : null;

  return (
    <div className="mt-3 space-y-3 border-s-2 border-neutral-200 ps-3">
      {/*
        Which hours this business gets.

        Only when there is more than one to choose between: with a single
        schedule the answer is already that one, and the dropdown would be a
        question with one answer.
      */}
      {schedules.length > 1 ? (
        <label className="block text-xs">
          <span className="mb-1 block text-neutral-600">{t('account:members.hoursLabel')}</span>
          <select
            className={inputCls}
            defaultValue={membership.settings.scheduleId ?? ''}
            onChange={(e) => save.mutate({ id: membership.id, scheduleId: e.target.value || null })}
          >
            {/*
              Named, because "my usual hours" hides the one thing somebody
              wants to know here: which hours those are.
            */}
            <option value="">
              {usual
                ? t('account:members.hoursDefaultNamed', { name: usual.name })
                : t('account:members.hoursDefault')}
            </option>
            {schedules
              .filter((s) => !s.isDefault)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
          <span className="mt-1 block text-neutral-500">{t('account:members.hoursHelp')}</span>
        </label>
      ) : null}
      {/*
        A dropdown only when there is a choice to make; with one tool or none it
        would be a control that does nothing. The sentence still says what will
        happen, because "where is the video setting" is otherwise a fair
        question for somebody who has connected one tool.
      */}
      {tools.length > 1 ? (
        <label className="block text-xs">
          <span className="mb-1 block text-neutral-600">{t('account:members.toolLabel')}</span>
          <select
            className={inputCls}
            defaultValue={membership.settings.preferredVideo ?? ''}
            onChange={(e) =>
              save.mutate({ id: membership.id, preferredVideo: e.target.value || null })
            }
          >
            {/* Named, because "no preference" otherwise hides which tool that is. */}
            <option value="">
              {t('account:members.toolAny', { tool: tools[0]?.label ?? '' })}
            </option>
            {tools.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      ) : tools.length === 1 ? (
        <p className="text-xs text-neutral-500">
          {t('account:members.toolOnly', { tool: tools[0]?.label ?? '' })}
        </p>
      ) : (
        <p className="text-xs text-neutral-500">{t('account:members.toolNone')}</p>
      )}

      <label className="block text-xs">
        <span className="mb-1 block text-neutral-600">{t('account:members.phoneLabel')}</span>
        <input
          className={inputCls}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onBlur={() =>
            phone !== (membership.settings.phone ?? '') &&
            save.mutate({ id: membership.id, phone: phone || null })
          }
        />
        <span className="mt-1 block text-neutral-500">{t('account:members.phoneHelp')}</span>
      </label>

      <label className="block text-xs">
        <span className="mb-1 block text-neutral-600">{t('account:members.noteLabel')}</span>
        <textarea
          className={inputCls}
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() =>
            note !== (membership.settings.note ?? '') &&
            save.mutate({ id: membership.id, note: note || null })
          }
        />
        <span className="mt-1 block text-neutral-500">{t('account:members.noteHelp')}</span>
      </label>

      {error ? <p className="text-xs text-red-600">{t(`errors:${error.code}`)}</p> : null}
    </div>
  );
}

function SubscribePrompt() {
  const { t } = useTranslation(['account']);
  return (
    <section className={cardCls}>
      <p className="text-sm text-neutral-700">{t('account:members.needsSubscription')}</p>
      <Link to="/app/billing" className={`${btnPrimary} mt-3 inline-flex`}>
        {t('account:members.goToBilling')}
      </Link>
    </section>
  );
}

function InviteForm() {
  const { t } = useTranslation(['account', 'errors']);
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const invite = useInviteMember();

  const error = invite.error instanceof ApiRequestError ? invite.error.code : null;

  return (
    <section className={cardCls}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          const address = email.trim();
          invite.mutate(address, {
            onSuccess: () => {
              setSentTo(address);
              setEmail('');
            },
          });
        }}
      >
        <div>
          <label htmlFor="member-email" className={labelCls}>
            {t('account:members.emailLabel')}
          </label>
          <input
            id="member-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setSentTo(null);
            }}
            required
            autoComplete="off"
            className={inputCls}
          />
          <p className="mt-1 text-xs text-neutral-500">{t('account:members.emailHelp')}</p>
        </div>

        {error ? <p className="text-sm text-red-600">{t(`errors:${error}`)}</p> : null}
        {sentTo ? (
          <p className="text-sm text-green-700">{t('account:members.sent', { email: sentTo })}</p>
        ) : null}

        <button
          type="submit"
          disabled={email.trim().length === 0 || invite.isPending}
          className={btnPrimary}
        >
          {t('account:members.invite')}
        </button>
      </form>
    </section>
  );
}

/**
 * Minutes as hours and minutes.
 *
 * Three forms rather than one, because "32h 0m" and "0h 45m" both read as
 * somebody padding a number. The minutes are kept whole — this is working
 * hours, where half past is a real answer and 32.5 is not how anybody says it.
 */
function hm(t: TFunction, total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return t('account:members.minutesOnly', { m });
  if (m === 0) return t('account:members.hoursOnly', { h });
  return t('account:members.hoursMinutes', { h, m });
}

/**
 * A week of hours in one line.
 *
 * Days that look the same are said once — "Mon–Fri 09:00–12:00" rather than
 * five lines of it — because the whole point is to be read at a glance beside
 * four other people's.
 */
function weeklySummary(t: TFunction, hours: NonNullable<WorkspaceMember['hours']>): string {
  const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
  const shape = (day: (typeof DAYS)[number]) =>
    (hours.weekly[day] ?? []).map((iv) => `${iv.start}–${iv.end}`).join(', ');

  const groups: Array<{ from: number; to: number; text: string }> = [];
  DAYS.forEach((day, i) => {
    const text = shape(day);
    const last = groups[groups.length - 1];
    if (last && last.text === text && last.to === i - 1) last.to = i;
    else groups.push({ from: i, to: i, text });
  });

  const said = groups
    .filter((g) => g.text !== '')
    .map((g) => {
      const days =
        g.from === g.to
          ? t(`account:members.days.${DAYS[g.from]}`)
          : `${t(`account:members.days.${DAYS[g.from]}`)}–${t(`account:members.days.${DAYS[g.to]}`)}`;
      return `${days} ${g.text}`;
    });

  if (said.length === 0) return t('account:members.hoursNone');
  return t('account:members.hoursSummary', {
    hours: said.join(' · '),
    schedule: hours.scheduleName,
  });
}

function MemberRow({ member, load }: { member: WorkspaceMember; load: MemberLoad | null }) {
  const { t } = useTranslation(['account', 'common', 'errors']);
  const remove = useRemoveMember();
  const resend = useResendInvitation();
  const [confirming, setConfirming] = useState(false);

  // Removal is refused while they still have appointments, and the count comes
  // back with the refusal — a bare "could not remove" would leave the owner
  // with nothing to act on.
  const error = remove.error instanceof ApiRequestError ? remove.error : null;
  const details = error?.details as { bookings?: number; eventTypes?: string[] } | undefined;
  const pendingBookings = error?.code === 'member_has_bookings' ? (details?.bookings ?? 0) : 0;
  // The API says which pages are in the way; repeating the generic message
  // without them leaves the owner hunting through every event type.
  const namedOn = error?.code === 'member_on_event_types' ? (details?.eventTypes ?? []) : [];

  return (
    <li className="flex flex-wrap items-start justify-between gap-3 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{member.name ?? member.email}</p>
        {member.name ? <p className="truncate text-xs text-neutral-500">{member.email}</p> : null}
        <p className="mt-1 text-xs text-neutral-500">
          {member.status === 'active'
            ? t('account:members.joined', {
                date: new Date(member.acceptedAt ?? member.invitedAt).toLocaleDateString(),
              })
            : t('account:members.pending')}
        </p>
        {/*
          Only once they have joined, and only when there is something to say.
          A member with no calendar still takes bookings — the appointment just
          never reaches a diary, which nothing else on this screen would reveal.
        */}
        {/*
          What they are actually offering, and what they are actually doing.
          Declared hours alone would miss somebody generous with their hours and
          busy with their own diary; the booked figure is what the other two are
          supposed to predict.
        */}
        {/*
          The hours they have given this business, which is the thing an owner
          is checking when they want to know that one person covers mornings
          and another afternoons. What the member set here, not what their own
          calendar happens to say.
        */}
        {member.status === 'active' && member.hours ? (
          <p className="mt-1 text-xs text-neutral-600">{weeklySummary(t, member.hours)}</p>
        ) : null}
        {member.status === 'active' && load ? (
          <p className="mt-1 text-xs text-neutral-500">
            {t('account:members.load', {
              free: hm(t, load.freeMinutes),
              declared: hm(t, load.declaredMinutes),
              booked: hm(t, load.bookedMinutes),
              count: load.bookings,
            })}
            {load.readable ? '' : ` · ${t('account:members.loadUnverified')}`}
          </p>
        ) : null}
        {member.status === 'active' && member.calendar !== 'ok' ? (
          <p className="mt-1 text-xs text-amber-700">
            {t(`account:members.calendar.${member.calendar}`)}
          </p>
        ) : null}
        {pendingBookings > 0 ? (
          <p className="mt-1 text-xs text-red-600">
            {t('account:members.hasBookings', { count: pendingBookings })}
          </p>
        ) : namedOn.length > 0 ? (
          <p className="mt-1 text-xs text-red-600">
            {t('account:members.namedOn', { eventTypes: namedOn.join(', ') })}
          </p>
        ) : error ? (
          <p className="mt-1 text-xs text-red-600">{t(`errors:${error.code}`)}</p>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {member.status === 'invited' ? (
          <button
            type="button"
            onClick={() => resend.mutate(member.id)}
            disabled={resend.isPending}
            className={btnSmall}
          >
            {resend.isSuccess ? t('account:members.resent') : t('account:members.resend')}
          </button>
        ) : null}
        {confirming ? (
          <>
            <button
              type="button"
              onClick={() => remove.mutate(member.id)}
              disabled={remove.isPending}
              className={btnDanger}
            >
              {t('account:members.confirmRemove')}
            </button>
            <button type="button" onClick={() => setConfirming(false)} className={btnSecondary}>
              {t('common:actions.cancel')}
            </button>
          </>
        ) : (
          <button type="button" onClick={() => setConfirming(true)} className={btnSmall}>
            {t('account:members.remove')}
          </button>
        )}
      </div>
    </li>
  );
}
